import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Copy, Download, ExternalLink, ShoppingBag, Calendar as CalIcon, Phone } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { tenant, user } = useAuth();
  const [twilioNumbers, setTwilioNumbers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [woo, setWoo] = useState({ configured: false, store_url: "" });
  const [wooForm, setWooForm] = useState({ store_url: "", consumer_key: "", consumer_secret: "" });
  const [wooBusy, setWooBusy] = useState(false);

  useEffect(() => {
    api.get("/calls/twilio/numbers").then((r) => setTwilioNumbers(r.data)).catch(() => {});
    api.get("/agents").then((r) => { setAgents(r.data); if (r.data[0]) setSelectedAgent(r.data[0].id); });
    api.get("/woocommerce/config").then((r) => setWoo(r.data));
  }, []);

  const inboundUrl = selectedAgent
    ? `${BACKEND}/api/twilio/inbound/${selectedAgent}`
    : "";

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const connectWoo = async () => {
    setWooBusy(true);
    try {
      await api.post("/woocommerce/config", wooForm);
      toast.success("WooCommerce connected");
      const { data } = await api.get("/woocommerce/config");
      setWoo(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to connect");
    } finally { setWooBusy(false); }
  };

  const disconnectWoo = async () => {
    await api.delete("/woocommerce/config");
    setWoo({ configured: false, store_url: "" });
    toast.success("Disconnected");
  };

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display font-extrabold text-3xl">Settings</h1>
        <p className="text-sm text-[#4B5563]">Workspace, integrations & team.</p>
      </div>

      <div className="hs-card p-6 space-y-3">
        <div className="font-display font-bold">Workspace</div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><div className="text-xs text-[#4B5563]">Name</div><div className="font-medium">{tenant?.name}</div></div>
          <div><div className="text-xs text-[#4B5563]">Slug</div><div className="font-mono">{tenant?.slug}</div></div>
          <div><div className="text-xs text-[#4B5563]">Industry</div><div className="font-medium">{tenant?.industry || "—"}</div></div>
          <div><div className="text-xs text-[#4B5563]">Plan</div><div className="font-medium">{tenant?.package_id}</div></div>
        </div>
      </div>

      <div className="hs-card p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display font-bold flex items-center gap-2"><Phone className="w-4 h-4" /> Twilio inbound webhook</div>
            <div className="text-xs text-[#4B5563] mt-1">Paste this URL into your Twilio number's Voice config to route inbound calls to a HelloSir agent.</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 items-end">
          <div className="col-span-1">
            <label className="text-xs font-medium block mb-1">Agent</label>
            <select data-testid="inbound-agent-select" value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)} className="w-full border border-[#E2E8F0] px-2 py-2 rounded-sm bg-white text-sm">
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input data-testid="inbound-webhook-url" readOnly value={inboundUrl} className="flex-1 border border-[#E2E8F0] px-2 py-2 rounded-sm text-xs font-mono" />
            <button onClick={() => copy(inboundUrl)} className="btn-outline text-xs flex items-center gap-1" data-testid="inbound-copy-button"><Copy className="w-3 h-3" /> Copy</button>
          </div>
        </div>
        <div className="text-xs text-[#4B5563]">
          <ol className="list-decimal ml-5 space-y-1">
            <li>Open Twilio console → Phone Numbers → your number.</li>
            <li>Under "A CALL COMES IN", select <strong>Webhook</strong> and paste the URL.</li>
            <li>Set HTTP method to <strong>POST</strong>. Save. Make sure your agent has been saved (creates VAPI assistant).</li>
          </ol>
        </div>
      </div>

      <div className="hs-card p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display font-bold flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> WooCommerce</div>
            <div className="text-xs text-[#4B5563] mt-1">Connect your store so agents can search products and recommend in real-time.</div>
          </div>
          {woo.configured && (
            <button onClick={disconnectWoo} className="btn-outline text-xs" data-testid="woo-disconnect-button">Disconnect</button>
          )}
        </div>
        {woo.configured ? (
          <div className="mt-3 text-sm">
            <div className="text-xs text-[#4B5563]">Connected store</div>
            <div className="font-mono">{woo.store_url}</div>
            <div className="text-xs text-[#10B981] mt-2">● live</div>
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            <input data-testid="woo-store-url-input" placeholder="https://your-store.com" value={wooForm.store_url} onChange={(e) => setWooForm({ ...wooForm, store_url: e.target.value })} className="w-full border border-[#E2E8F0] px-2 py-2 rounded-sm text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="woo-key-input" placeholder="Consumer key (ck_...)" value={wooForm.consumer_key} onChange={(e) => setWooForm({ ...wooForm, consumer_key: e.target.value })} className="border border-[#E2E8F0] px-2 py-2 rounded-sm text-sm" />
              <input data-testid="woo-secret-input" placeholder="Consumer secret (cs_...)" type="password" value={wooForm.consumer_secret} onChange={(e) => setWooForm({ ...wooForm, consumer_secret: e.target.value })} className="border border-[#E2E8F0] px-2 py-2 rounded-sm text-sm" />
            </div>
            <div className="text-xs text-[#4B5563]">Get keys from WooCommerce → Settings → Advanced → REST API → Add key (Read permission is enough).</div>
            <button onClick={connectWoo} disabled={wooBusy || !wooForm.store_url} className="btn-primary text-sm" data-testid="woo-connect-button">{wooBusy ? "Verifying…" : "Connect store"}</button>
          </div>
        )}
      </div>

      <div className="hs-card p-6">
        <div className="font-display font-bold flex items-center gap-2"><CalIcon className="w-4 h-4" /> Google Calendar</div>
        <div className="text-xs text-[#4B5563] mt-1">Sync appointments and check availability in real-time. Configure OAuth in your Google Cloud project, then connect below.</div>
        <button data-testid="google-connect-button" disabled className="btn-outline text-sm mt-3 opacity-60 cursor-not-allowed">Connect Google Calendar (coming soon)</button>
      </div>

      <div className="hs-card p-6">
        <div className="font-display font-bold flex items-center gap-2"><Download className="w-4 h-4" /> WordPress plugin</div>
        <div className="text-xs text-[#4B5563] mt-1">Drop the chat widget into any WordPress site in 2 minutes.</div>
        <a href={`${BACKEND}/api/wordpress-plugin.zip`} download data-testid="wp-plugin-download" className="btn-primary text-sm inline-flex items-center gap-2 mt-3">
          <Download className="w-4 h-4" /> Download .zip
        </a>
        <ol className="text-xs text-[#4B5563] list-decimal ml-5 mt-3 space-y-1">
          <li>WP Admin → Plugins → Add New → Upload Plugin → choose the file.</li>
          <li>Activate → Settings → HelloSir.ai → paste your Agent ID.</li>
          <li>Save. The chat widget appears on every page.</li>
        </ol>
      </div>

      <div className="hs-card p-6">
        <div className="font-display font-bold">Connected integrations</div>
        <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
          <div className="border border-[#E2E8F0] p-3"><div className="font-medium">OpenAI</div><div className="text-xs text-[#4B5563] mt-1">Configured ✓</div></div>
          <div className="border border-[#E2E8F0] p-3"><div className="font-medium">Twilio</div><div className="text-xs text-[#4B5563] mt-1">{twilioNumbers.length} number(s)</div></div>
          <div className="border border-[#E2E8F0] p-3"><div className="font-medium">VAPI</div><div className="text-xs text-[#4B5563] mt-1">Configured ✓</div></div>
          <div className="border border-[#E2E8F0] p-3"><div className="font-medium">Stripe</div><div className="text-xs text-[#4B5563] mt-1">Test mode</div></div>
        </div>
      </div>

      <div className="hs-card p-6">
        <div className="font-display font-bold">Your account</div>
        <div className="text-sm mt-2">{user?.name} · <span className="font-mono">{user?.email}</span></div>
        <div className="text-xs text-[#4B5563]">role: {user?.role}</div>
      </div>

      <AccountDangerZone />
    </div>
  );
}

function AccountDangerZone() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [confirm, setConfirm] = React.useState(false);
  const scheduledAt = user?.deletion_scheduled_at;

  const schedule = async () => {
    setBusy(true);
    try {
      await api.post("/auth/account/schedule-deletion");
      toast.success("Account scheduled for deletion. You'll get a confirmation email.");
      setConfirm(false);
      await refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not schedule deletion");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/auth/account/cancel-deletion");
      toast.success("Deletion cancelled — your account is safe.");
      await refresh?.();
    } catch (e) {
      toast.error("Could not cancel");
    } finally { setBusy(false); }
  };

  return (
    <div className="hs-card p-6 border-[#EF4444]/30" data-testid="account-danger-zone">
      <div className="font-display font-bold text-[#EF4444]">Danger zone</div>

      {scheduledAt ? (
        <div className="mt-4 p-4 rounded bg-[#FEE2E2] border border-[#EF4444]/30 text-[#7F1D1D] text-sm">
          <div className="font-bold">Account scheduled for deletion on {new Date(scheduledAt).toLocaleDateString()}</div>
          <p className="mt-1">All agents, conversation logs and credits will be permanently removed. You can cancel any time before then.</p>
          <button onClick={cancel} disabled={busy} className="btn-primary text-sm mt-3" data-testid="cancel-deletion-btn">
            {busy ? "Cancelling…" : "Cancel deletion"}
          </button>
        </div>
      ) : !confirm ? (
        <>
          <p className="text-sm text-[#4B5563] mt-2">
            Schedule your account for deletion. You have <strong>30 days</strong> to change your mind — after that everything is permanently removed.
          </p>
          <button onClick={() => setConfirm(true)} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded text-sm border border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2]" data-testid="delete-account-btn">
            Delete my account
          </button>
        </>
      ) : (
        <div className="mt-4 p-4 rounded bg-[#FFFBEB] border border-[#F59E0B]/40">
          <p className="text-sm">This will schedule <span className="font-mono">{user?.email}</span> for deletion in 30 days. We'll email you a confirmation.</p>
          <div className="flex gap-2 mt-3">
            <button onClick={schedule} disabled={busy} className="px-4 py-2 rounded text-sm bg-[#EF4444] text-white hover:bg-[#DC2626]" data-testid="confirm-delete-btn">
              {busy ? "Scheduling…" : "Yes, schedule deletion"}
            </button>
            <button onClick={() => setConfirm(false)} className="btn-outline text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
